import { createFileRoute } from "@tanstack/react-router";

import { PageChrome } from "../components/PageChrome";
import { StoreCta } from "../components/StoreCta";
import { COMPANY, COMPANY_ADDRESS, COMPANY_NO, SUPPORT } from "../lib/legal";
import {
  breadcrumbJsonLd,
  canonical,
  documentHeaders,
  organizationJsonLd,
  socialMeta,
} from "../lib/seo";

const TITLE = "Who makes Fazly — About";
const DESCRIPTION =
  "Who builds Fazly, where the company is registered, how the app is paid for, and what it does with the photographs you give it. Written plainly, checkable.";

/**
 * `/about` — the page that was a 404.
 *
 * ── Why a small site needs one ───────────────────────────────────────────────
 *
 * Two audiences read this page and neither is browsing. The first is a person
 * deciding whether to hand photographs of their own face to software made by
 * strangers; the question they have is who, and it is a fair one — and it is a
 * sharper question here than on almost any other kind of app. The second is a
 * search engine
 * or an assistant trying to resolve "Fazly" to an entity it can attach
 * facts to — and an entity with a registered company number, an address and a
 * support address is one it can, while a brand name with none of those is a
 * string.
 *
 * ── What is deliberately NOT here ────────────────────────────────────────────
 *
 * A named author with a photograph and a paragraph of credentials. Every guide
 * to this subject asks for one, and inventing a person is the single easiest
 * thing on that list to do and the only one that would be a lie. The company is
 * real, its number is checkable at Companies House, and until a human decides to
 * put their own name to this writing, the honest byline is the organisation.
 *
 * Everything stated below is either a fact from `lib/legal.ts` — the same
 * constants the privacy policy and the terms are built from, checked by
 * `legal.test.ts` against what the app actually does — or a description of
 * behaviour anyone can verify by using it.
 */
export const Route = createFileRoute("/about")({
  headers: documentHeaders,
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      ...socialMeta({
        title: TITLE,
        description: DESCRIPTION,
        path: "/about",
        lang: "en",
      }),
      { "script:ld+json": organizationJsonLd() },
      {
        "script:ld+json": breadcrumbJsonLd([
          { name: "Fazly", path: "/" },
          { name: "About", path: "/about" },
        ]),
      },
    ],
    links: [{ rel: "canonical", href: canonical("/about") }],
  }),
  component: About,
});

function Section({
  id,
  title,
  children,
}: {
  id: string;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="mt-12">
      <h2
        id={id}
        className="scroll-mt-24 font-display text-2xl font-bold text-ink"
      >
        {title}
      </h2>
      <div className="mt-4 flex flex-col gap-4 leading-[1.75] text-body">
        {children}
      </div>
    </section>
  );
}

function About() {
  return (
    <PageChrome>
      <div className="mx-auto w-full max-w-[68ch]">
        <p className="font-display text-xs font-bold tracking-[0.16em] text-scripture uppercase">
          about
        </p>
        <h1 className="mt-3 font-display text-[clamp(1.9rem,6vw,2.9rem)] leading-[1.15] font-bold tracking-tight text-ink text-balance">
          Who makes Fazly
        </h1>
        <p className="mt-4 text-[clamp(1.05rem,2.4vw,1.2rem)] leading-relaxed text-ink2">
          A short, checkable answer to the questions worth asking before you
          send photographs of your face to a stranger&rsquo;s server.
        </p>

        <Section id="who" title="The company">
          <p>
            Fazly is built and published by {COMPANY}, a company registered in
            England and Wales, number {COMPANY_NO}, at {COMPANY_ADDRESS}. That
            number is searchable at Companies House and it is the same one on
            the{" "}
            <a
              href="/privacy"
              className="font-semibold text-brand underline decoration-hair underline-offset-4"
            >
              privacy policy
            </a>{" "}
            and the{" "}
            <a
              href="/terms"
              className="font-semibold text-brand underline decoration-hair underline-offset-4"
            >
              terms
            </a>
            .
          </p>
          <p>
            It is a small team, not a large one, and there is no venture money
            behind it deciding what the app has to become.
          </p>
        </Section>

        <Section id="why" title="Why it exists">
          <p>
            There are a lot of apps that will put a number on your face. Almost
            all of them tell almost everybody the same thing, somewhere around
            seven out of ten, because a flattering number is easier to sell than
            an accurate one. A score that was always going to be that score
            tells you nothing, and a plan built on top of it is decoration.
          </p>
          <p>
            Fazly is built on the opposite bet: that the number is only worth
            anything if it could have come out differently, and that what you do
            with it matters more than the number does. So the scan returns seven
            scores and the thirty measurements under them, it says which single
            area is worth your attention first, and it turns that into a routine
            you can actually work — the part that changes anything. An honest
            scorer that hands out fours is worth more than a kind one that hands
            out sevens.
          </p>
        </Section>

        <Section id="money" title="How it is paid for">
          <p>
            By people buying it. There is no advertising in the app, and nothing
            you give it is sold. The scan and your overall score are free; the
            full breakdown, the roadmap and the rest of Pro are what you pay
            for, and the{" "}
            <a
              href="/terms#subscriptions"
              className="font-semibold text-brand underline decoration-hair underline-offset-4"
            >
              terms
            </a>{" "}
            say exactly which plans exist.
          </p>
          <p>
            There is no free trial on any of them. The weekly plan opens at a
            reduced price for the first week, which is a discount and not a
            trial — money moves on day one, and we would rather say so here than
            let you find out on a statement. Billing is handled by Apple and
            Google, so cancelling happens in your own device settings and never
            requires asking us.
          </p>
        </Section>

        <Section id="data" title="What it does with your photographs">
          <p>
            They are used for the analysis and deleted within seven days. They
            go to the provider that scores them and to nobody else, they are
            never shown to another user, and they are never used to train
            anything. There is no face recognition anywhere in Fazly — nothing
            in it tries to work out who you are, match you against another
            photograph, or build a template of your face. It measures
            proportions, returns numbers, and throws the picture away.
          </p>
          <p>
            You can delete your account and everything in it from inside the app
            or from{" "}
            <a
              href="/delete"
              className="font-semibold text-brand underline decoration-hair underline-offset-4"
            >
              this website
            </a>
            , without emailing anyone. The{" "}
            <a
              href="/privacy#processors"
              className="font-semibold text-brand underline decoration-hair underline-offset-4"
            >
              privacy policy
            </a>{" "}
            names every company your data actually reaches, with a link to each
            one&rsquo;s own policy — including the ones it is uncomfortable to
            list. That list is checked against the code by a test rather than
            maintained by memory.
          </p>
        </Section>

        <Section id="writing" title="About the writing">
          <p>
            The{" "}
            <a
              href="/how-to"
              className="font-semibold text-brand underline decoration-hair underline-offset-4"
            >
              guides on this site
            </a>{" "}
            are published by {COMPANY} rather than under an individual byline.
            We would rather attribute them to the organisation that stands
            behind them than invent a persona, and everything in them is either
            something the app does — which you can check — or a claim about
            faces that says where it comes from.
          </p>
          <p>
            If something in them is wrong, write to{" "}
            <a
              href={`mailto:${SUPPORT}`}
              className="font-semibold text-brand underline decoration-hair underline-offset-4"
            >
              {SUPPORT}
            </a>{" "}
            and it will be corrected rather than quietly left.
          </p>
        </Section>

        <Section id="contact" title="Getting in touch">
          <p>
            {SUPPORT}, read by a person. The{" "}
            <a
              href="/support"
              className="font-semibold text-brand underline decoration-hair underline-offset-4"
            >
              support page
            </a>{" "}
            answers the questions that arrive most often — what happens to your
            photographs, a scan that failed, subscriptions and account deletion.
          </p>
        </Section>

        <StoreCta from="about" />
      </div>
    </PageChrome>
  );
}
